import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { getWorkOSSession } from './utils/workos';
import Loading from './ui/Loading.tsx';

const REQUIRED_ROLE = 'my-services-user';

const AccessDenied: React.FC<{ email: string; authProvider: string }> = ({ email, authProvider }) => {
  const consoleUrl = import.meta.env.VITE_LANDING || 'https://console.yuso.io';

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="text-center space-y-4 mt-24">
        <h2 className="text-2xl font-semibold text-primary-600 mb-6">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access this tool.</p>
        {email && (
          <p className="text-sm text-gray-400">
            Logged in as <strong>{email}</strong> via {authProvider}
          </p>
        )}
        <div className="pt-4">
          <button
            onClick={() => { window.location.href = consoleUrl; }}
            className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Go to console
          </button>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [deniedEmail, setDeniedEmail] = useState('');
  const [deniedProvider, setDeniedProvider] = useState('');

  useEffect(() => {
    const check = async () => {
      // getWorkOSSession handles Cognito + WorkOS, checks expiry,
      // calls live API for roles, and redirects to login if no session.
      const session = await getWorkOSSession();

      if (!session) {
        // getWorkOSSession already redirected to login
        return;
      }

      if (session.authProvider === 'workos') {
        const role = session.role || '';
        const roles = session.roles || [];
        const hasAccess = role === REQUIRED_ROLE || roles.includes(REQUIRED_ROLE);

        if (hasAccess) {
          setIsValid(true);
        } else {
          setDeniedEmail(session.user.email);
          setDeniedProvider('workos');
          setAccessDenied(true);
        }
      } else {
        // Cognito / Google — always allow
        setIsValid(true);
      }

      setIsChecked(true);
    };

    check();
  }, []);

  if (!isChecked) return <Loading />;
  if (accessDenied) return <AccessDenied email={deniedEmail} authProvider={deniedProvider} />;
  if (!isValid) return null;
  return <Outlet />;
};

export default ProtectedRoute;
